import { useMemo } from 'react';
import { Card, Col, Row, Statistic, Table, Typography, type TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { listTenantSummaries, type TenantSummaryRow } from './api';

const moneyYuan = (cents: number) => `¥ ${(cents / 100).toFixed(2)}`;

export function DashboardPage() {
  const query = useQuery({
    queryKey: ['operator', 'dashboard', 'tenants'],
    queryFn: listTenantSummaries,
  });

  const totals = useMemo(() => {
    const rows = query.data ?? [];
    return rows.reduce(
      (acc, row) => {
        acc.products += row.summary.productCount;
        acc.orders += row.summary.orderCount;
        acc.pending += row.summary.pendingOrders;
        acc.revenueCents += row.summary.totalRevenueCents;
        return acc;
      },
      { products: 0, orders: 0, pending: 0, revenueCents: 0 },
    );
  }, [query.data]);

  const columns: TableProps<TenantSummaryRow>['columns'] = [
    { title: '租户 ID', dataIndex: 'tenantId', width: 90 },
    { title: 'Slug', dataIndex: 'slug', width: 160 },
    { title: '名称', dataIndex: 'name' },
    {
      title: '商品',
      dataIndex: ['summary', 'productCount'],
      width: 100,
      align: 'right',
    },
    {
      title: '订单',
      dataIndex: ['summary', 'orderCount'],
      width: 100,
      align: 'right',
    },
    {
      title: '待处理',
      dataIndex: ['summary', 'pendingOrders'],
      width: 100,
      align: 'right',
    },
    {
      title: '成交额',
      dataIndex: ['summary', 'totalRevenueCents'],
      width: 140,
      align: 'right',
      render: (cents: number) => moneyYuan(cents),
    },
  ];

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Operator Dashboard</div>
          <Typography.Title level={3} className="mall-page-title">
            平台概览
          </Typography.Title>
          <p className="mall-page-description">跨租户汇总商品、订单、待处理事项和成交额。</p>
        </div>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card className="mall-metric-card">
            <Statistic title="租户数量" value={(query.data ?? []).length} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="mall-metric-card">
            <Statistic title="商品总数" value={totals.products} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="mall-metric-card">
            <Statistic title="订单总数" value={totals.orders} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="mall-metric-card">
            <Statistic
              title="累计成交额"
              value={(totals.revenueCents / 100).toFixed(2)}
              prefix="¥"
            />
          </Card>
        </Col>
      </Row>

      <Card title="按租户拆分" className="mall-table-card">
        <Table<TenantSummaryRow>
          rowKey="tenantId"
          columns={columns}
          loading={query.isLoading}
          dataSource={query.data ?? []}
          pagination={false}
          size="small"
          scroll={{ x: 820 }}
          locale={{ emptyText: '暂无租户数据' }}
        />
        {query.isError && (
          <Typography.Paragraph type="danger" style={{ marginTop: 16 }}>
            加载失败：{query.error instanceof Error ? query.error.message : '未知错误'}
          </Typography.Paragraph>
        )}
      </Card>
    </div>
  );
}
